import {
  AgenticComponents,
  type AgenticIntegration,
} from "@p0security/integrations-agentic-shared/components";
import { isValidInstance } from "@p0security/integrations-agentic-shared/mcp/instance";
import {
  familyServerId,
  mcpServerAvailability,
  mcpServerFamilies,
  mcpServers,
  predefinedMcpServer,
} from "@p0security/integrations-agentic-shared/mcp/registry";
import { AgenticRegistration } from "@p0security/integrations-agentic-shared/registry";
import type { FrontendInstallDomain } from "@p0security/integrations-installer";
import { otherInstalledIntegration } from "@p0security/integrations-installer";
import type { DynamicProvider } from "@p0security/shared/install/types";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import type { AgenticServerCredential } from "@p0security/shared/types/agentic/credential";
import type { McpServerSpec } from "@p0security/shared/types/agentic/mcp";
import type { EmptyAccess } from "@p0security/shared/types/lifecycle";
import { Descriptions, Drawer, Flex, Typography } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { isKeyOf } from "@p0security/util/types/is";
import { compact, size, sortBy } from "lodash-es";
import type React from "react";
import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { DetailsDescriptions } from "./DetailsDescriptions.tsx";
import { AllIntegrationLogos } from "./IntegrationLogo.tsx";
import { agenticGatewayFieldInstaller } from "./shared.tsx";
import type { AgenticInstaller } from "./types.ts";

type McpServerConfig = AgenticIntegration["server"][string];

type ServerInstaller = AgenticInstaller["components"]["server"];
type CredentialInstaller = ServerInstaller["items"]["credential"];
type FederationInstaller = NonNullable<CredentialInstaller>["options"];
type GenericCredentialInstaller =
  NonNullable<CredentialInstaller>["options"]["generic"];
type ImpersonationCredentialInstaller =
  NonNullable<CredentialInstaller>["options"]["impersonation"];
type NativeCredentialInstaller =
  NonNullable<CredentialInstaller>["options"]["native"];
type OAuthInstaller = NonNullable<CredentialInstaller>["options"]["oauth"];
type DefinitionInstaller = NonNullable<ServerInstaller["items"]["definition"]>;
type CustomInstaller = DefinitionInstaller["options"]["custom"];
type CustomControlInstaller = NonNullable<CustomInstaller>["items"]["control"];
type NativeControlInstaller =
  NonNullable<CustomControlInstaller>["options"]["native"];
type P0Installer = DefinitionInstaller["options"]["p0"];

type OptionProvider = DynamicProvider<
  FrontendInstallDomain<AgenticIntegration>,
  McpServerConfig
>;

type NativeClaimProps = {
  item: McpServerConfig;
};

const RequestKeys = ["parameters", "generated"] as const;

const WHITESPACE = /\s+/;

const { Link: HrefLink, Text } = Typography;

const accessOf = ({ definition }: McpServerConfig): EmptyAccess | null => {
  if (definition.type !== "custom" || !definition.control) return null;
  const { control } = definition;
  if (control.type === "annotation")
    return AgenticRegistration.accesses.annotation;
  if (control.type === "tool") return AgenticRegistration.accesses.tool;
  const key = control.resource;
  const [access] = (control.prefixArgs ?? "").split(WHITESPACE);
  if (!key || !isKeyOf(integrationRegistry, key) || !access) return null;
  const { accesses } = integrationRegistry[key];
  if (!isKeyOf(accesses, access)) return null;
  return accesses[access];
};

const predefinedSpecOf = ({
  definition,
  gateway,
}: McpServerConfig): McpServerSpec | undefined => {
  if (definition.type === "custom") return;
  const spec = predefinedMcpServer(definition.id);
  if (!spec) return;
  return typeof spec === "function"
    ? spec({ gateway, server: definition.id, instance: definition.instance })
    : spec;
};

const credsOf = (item: McpServerConfig): AgenticServerCredential | undefined =>
  predefinedSpecOf(item)?.credential;

// Simulates a validation error: the element validator cannot see which
// server is selected
const instanceLabeler = (_c: any, _id: string, item: McpServerConfig) => {
  const { definition } = item;
  if (definition.type !== "p0") return;
  const spec = predefinedSpecOf(item);
  if (!spec) return;
  if (!spec.instance) {
    return <Text type="secondary">This server does not use an instance</Text>;
  }
  const { description, label, placeholder } = spec.instance;
  return (
    <Flex vertical gap={8}>
      <div>{label}</div>
      <Text type="secondary">{description}</Text>
      {placeholder && <Text type="secondary">For example: {placeholder}</Text>}
      {definition.instance && !isValidInstance(spec, definition.instance) && (
        <Text type="danger">This value is not valid for this server</Text>
      )}
    </Flex>
  );
};

const p0BackendProvider: OptionProvider = async (context, { gateway }) => {
  const out: Awaited<ReturnType<OptionProvider>> = [];
  for (const [server, spec] of Object.entries(mcpServers)) {
    const resolved =
      typeof spec === "function" ? spec({ gateway, server }) : spec;
    if (mcpServerAvailability[server]?.(context.installed) ?? true) {
      out.push({
        iconUrl: resolved.iconUrl ?? AgenticRegistration.logoUrl,
        id: server,
        label: resolved.label,
      });
    }
  }
  // One server per installed item of each family.
  for (const [family, { items, spec }] of Object.entries(mcpServerFamilies)) {
    for (const item of items(context.installed ?? {})) {
      const itemSpec = spec(item);
      if (!itemSpec) continue;
      const id = familyServerId(family, item);
      const resolved =
        typeof itemSpec === "function"
          ? itemSpec({ gateway, server: id })
          : itemSpec;
      out.push({
        iconUrl: resolved.iconUrl ?? AgenticRegistration.logoUrl,
        id,
        label: resolved.label,
      });
    }
  }
  return sortBy(out, "label");
};

// Simulates a validation error, even though this logic is too complicated for a
// validator
const nativeControlPrefixArgsLabeler = (
  _c: any,
  _id: string,
  item: McpServerConfig
) => {
  const { definition } = item;
  if (definition.type !== "custom" || definition.control?.type !== "native")
    return;
  const access = accessOf(item);
  if (access) return;
  const { resource } = definition.control;
  if (!isKeyOf(integrationRegistry, resource)) return;
  return (
    <Flex vertical gap={8}>
      <div>
        {
          AgenticComponents.server.schema.definition.options.custom.schema
            .control.options.native.schema.prefixArgs.description
        }
      </div>
      <div>
        <Text type="danger">
          First token in prefix arguments must be one of{" "}
          {Object.keys(integrationRegistry[resource].accesses)
            .map((s) => `'${s}'`)
            .join(", ")}
        </Text>
      </div>
    </Flex>
  );
};

const nativeControlInstaller: NativeControlInstaller = {
  items: {
    prefixArgs: {
      labeler: nativeControlPrefixArgsLabeler,
    },
    resource: {
      optionProvider: async (c) =>
        compact(
          Object.keys(c.installed).map((id) =>
            isKeyOf(integrationRegistry, id) &&
            integrationRegistry[id].requestType === "requestable"
              ? {
                  id,
                  label: (
                    <Flex gap={8} align="center">
                      {AllIntegrationLogos[id]}
                      <div>{integrationRegistry[id].label}</div>
                    </Flex>
                  ),
                }
              : null
          )
        ),
    },
  },
};

const customInstaller: CustomInstaller = {
  items: {
    control: {
      options: {
        native: nativeControlInstaller,
      },
    },
  },
};

const p0Installer: P0Installer = {
  items: {
    id: {
      // @ts-expect-error this _should_ type check
      optionProvider: p0BackendProvider,
    },
    instance: {
      labeler: instanceLabeler,
    },
  },
};

/** Filters allowed credentials for predefined servers to those that
 *  match the server definition.
 */
const credentialProvider: NonNullable<CredentialInstaller>["optionsProvider"] =
  async (_context, item) => {
    const creds = credsOf(item);
    if (!creds)
      return widetype.keys(AgenticComponents.server.schema.credential.options);
    // TODO: Break this hard-coding
    if (creds.type === "wif" || creds.type === "rds-iam") {
      return creds.type === "rds-iam" || creds.csp === "aws"
        ? ["aws"]
        : [creds.csp];
    } else {
      return [creds.type];
    }
  };

const awsFederationInstaller: FederationInstaller["aws"] = {
  prerequisiteMessages: async (_c, _id, { definition }) =>
    definition.type === "custom"
      ? "Custom servers are currently not available with AWS WIF credentials"
      : null,
  items: {
    provider: {
      optionProvider: otherInstalledIntegration({
        integration: "aws-oidc",
        component: "identity",
      }),
    },
  },
};

const gcpFederationInstaller: FederationInstaller["gcp"] = {
  items: {
    provider: {
      optionProvider: otherInstalledIntegration({
        integration: "gcp-wif",
        component: "identity",
      }),
    },
  },
};

const genericCredentialInstaller: GenericCredentialInstaller = {};

const oauthCredentialInstaller: OAuthInstaller = {
  items: {},
};

const NativeClaims: React.FC<NativeClaimProps> = ({ item }) => {
  const [search, setSearch] = useSearchParams();
  const closeClaims = useCallback(() => {
    const next = new URLSearchParams(search);
    next.delete("claims");
    setSearch(next);
  }, [search, setSearch]);

  const descriptions = useMemo(() => {
    const access = accessOf(item);
    return access ? (
      <Flex vertical gap={8}>
        {RequestKeys.map((r) =>
          size(access[r]) ? (
            <DetailsDescriptions columns={1} key={r}>
              {sortBy(Object.entries(access[r]), "0").map(([k, v]) => (
                <Descriptions.Item
                  label={
                    <Text
                      copyable
                      className="[&:not(:hover)_button.ant-typography-copy]:invisible text-nowrap"
                    >
                      request.{r === "parameters" ? "permission" : r}.{k}
                    </Text>
                  }
                  key={k}
                >
                  <Flex vertical>
                    <div>
                      {v.label ?? (
                        <Text type="secondary">(no description)</Text>
                      )}
                    </div>
                    <div>
                      <Text type="secondary">{v.description}</Text>
                    </div>
                  </Flex>
                </Descriptions.Item>
              ))}
            </DetailsDescriptions>
          ) : null
        )}
      </Flex>
    ) : null;
  }, [item]);

  return (
    <>
      <Flex vertical>
        <div>
          See the{" "}
          <HrefLink
            href="https://shopify.github.io/liquid/"
            rel="noopener"
            target="_blank"
          >
            Liquid docs
          </HrefLink>{" "}
          for usage instructions
        </div>
        <div>
          See <Link to={{ search: "claims" }}>available variables</Link>
        </div>
      </Flex>
      <Drawer
        mask={false}
        onClose={closeClaims}
        open={search.has("claims")}
        title="Available template variables"
        width="50%"
      >
        {descriptions ?? (
          <div>
            Select a resource and valid access-command prefix to display
            variables
          </div>
        )}
      </Drawer>
    </>
  );
};

const impersonationCredentialInstaller: ImpersonationCredentialInstaller = {};

const nativeCredentialInstaller: NativeCredentialInstaller = {
  items: {
    claims: {
      labeler: (_c, _id, item) => <NativeClaims item={item} />,
    },
  },
};

export const agenticServerInstaller: ServerInstaller = {
  items: {
    credential: {
      optionsProvider: credentialProvider,
      options: {
        aws: awsFederationInstaller,
        gcp: gcpFederationInstaller,
        generic: genericCredentialInstaller,
        impersonation: impersonationCredentialInstaller,
        native: nativeCredentialInstaller,
        oauth: oauthCredentialInstaller,
      },
    },
    definition: {
      options: {
        custom: customInstaller,
        p0: p0Installer,
      },
    },
    gateway: agenticGatewayFieldInstaller,
  },
};
